import React, { useState } from "react";
import { Overline } from "@/components/Primitives";

// Objection-handling FAQ. Reusable on the services hub, service detail pages,
// contact page, and anywhere else pricing anxiety needs to be addressed.
const QUESTIONS = [
  {
    q: "Why are some prices not listed?",
    a: "Some services can be priced simply. Larger brand, website, and marketing systems should not be priced before the actual problem is understood. Kostaja scopes those projects after reviewing what exists, what needs to change, and what outcome the work is meant to support.",
  },
  {
    q: "Will this be too expensive?",
    a: "Kostaja will not push a full project when an audit, fixed-scope service, or digital resource is the better starting point. The goal is to choose the right level of help, not the largest possible engagement.",
  },
  {
    q: "Can I get a ballpark before committing?",
    a: "Yes. After a short review, Kostaja can provide a realistic scope, path, and investment range before a full proposal is prepared.",
  },
  {
    q: "Do I have to commit on the first call?",
    a: "No. The first step is to understand the issue and decide whether the right next move is an audit, a fixed-scope service, a custom project, ongoing support, or a resource you can use yourself.",
  },
  {
    q: "What happens after I inquire?",
    a: "Kostaja reviews your business, website, current materials, goals, and constraints. If there is a fit, you receive a recommended path, scope, timeline, and pricing structure before work begins.",
  },
  {
    q: "Why not use one standard package for everyone?",
    a: "A standard package can work when the deliverable is simple. Larger work depends on what already exists, what needs to change, how many pieces are involved, and how directly the project supports the next business outcome.",
  },
  {
    q: "Can I start small?",
    a: "Yes. If a larger project is not the right first step, Kostaja may recommend an audit, strategy call, fixed-scope service, or digital resource before a larger engagement.",
  },
  {
    q: "What if I do not know what I need?",
    a: "Start with a strategy call or browse by need. Kostaja can help determine whether the issue is brand, website, content, local visibility, sales materials, or a combination of them.",
  },
];

const PricingFAQ = ({ compact = false, testId = "pricing-faq" }) => {
  const [open, setOpen] = useState(0);
  const items = compact ? QUESTIONS.slice(0, 4) : QUESTIONS;

  return (
    <section
      data-testid={testId}
      className="bg-black border-b border-white/10"
    >
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <Overline color="yellow" className="mb-3">
            Pricing questions
          </Overline>
          <h2 className="text-3xl lg:text-5xl font-black tracking-tighter leading-[0.95]">
            No pressure. No mystery.
          </h2>
          <p className="mt-6 text-white/70 max-w-md text-base leading-relaxed">
            Kostaja uses commercial judgment to recommend the right level of help. Some services are fixed-scope. Some are scoped after review. Neither is a sales trick.
          </p>
        </div>
        <div className="lg:col-span-7">
          <ul className="border-y border-white/15">
            {items.map((it, idx) => {
              const isOpen = open === idx;
              return (
                <li key={it.q} className="border-b border-white/15 last:border-b-0">
                  <button
                    type="button"
                    data-testid={`${testId}-q-${idx}`}
                    onClick={() => setOpen(isOpen ? -1 : idx)}
                    className="w-full flex items-start justify-between gap-6 py-5 text-left hover:text-[#FFC600] transition-colors"
                    aria-expanded={isOpen}
                  >
                    <span className="text-lg lg:text-xl font-bold tracking-tight leading-snug">
                      {it.q}
                    </span>
                    <span
                      className="shrink-0 mt-1 text-[#FFC600] text-xl leading-none"
                      aria-hidden
                    >
                      {isOpen ? "−" : "+"}
                    </span>
                  </button>
                  {isOpen ? (
                    <div
                      data-testid={`${testId}-a-${idx}`}
                      className="pb-6 pr-10 text-white/80 text-base leading-relaxed"
                    >
                      {it.a}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default PricingFAQ;
